// FT3D table chat delivery (owner 2026-09-15).
//
// The frames arrive here and are re-published for the HUD's chat surface (ft3d-hud/chat.ts), which owns the
// transcript, the bubble, the gesture and the dedupe:
//   `ft3d_chat`      → `ft:ft3d-chat`       the log line
//   `ft3d_emote_cue` → `ft:ft3d-emote-cue`  the emote's PERFORMANCE, pushed ahead of the line (owner 2026-09-16)
// This module adds the one thing that belongs to the audio bundle: a voiced emote's bark, in the speaker's own
// voice, chosen by a seed every client shares so the whole table hears the same take.

import type { WSMessage } from './types';
import { loadAudioCatalog, loadVoiceShard, pickAudioTake, seededRandom } from '../shared/audio-catalog';
import { entityVoiceSlug } from '../shared/roll-sound-route';
import { playChatVoiceLine } from './roll-audio-mixer';
import { pushEphemeralTruth, type EphemeralTruth } from '../shared/ephemeral-truth';
import { actorEncounterFor, actorRowFor } from './movement-truth';
import { abilityTruthReaders, abilityTruthCampaign } from './ability-truth';
import { gmStatusAppliedFrame } from '../janus-engine/transport/gm-applied-frame';
import { handleBasePatch } from './base-patch';
import { rowScalar } from '../encounter-map/ft3d/perception';
import type { AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import { ws } from './state';
import { truthWireBytes } from './truth-transport';

type ChatMessage = Extract<WSMessage, { type: 'ft3d_chat' }>;
type EmoteCueMessage = Extract<WSMessage, { type: 'ft3d_emote_cue' }>;
const savedChatOutbox = new Map<string, { wire: string; socket?: WebSocket }>();
function sendSavedChat(pending: { wire: string; socket?: WebSocket }): void {
    if (ws && pending.socket !== ws && pushEphemeralTruth(JSON.parse(pending.wire) as EphemeralTruth)) pending.socket = ws;
}
window.addEventListener('ft:ws-channel-subscribed', () => {
    for (const pending of savedChatOutbox.values()) sendSavedChat(pending);
});

/** Approved takes first, then unreviewed — the take-selection policy every voice lookup uses. */
function emoteTiers(voice: string, pool: string): string[][] {
    return [[`voice/${voice}__${pool}`], [`voice~/${voice}__${pool}`]];
}

/** Voiced emote key → recorded pool. Server twin: ft_chat_voiced_emotes(). */
const EMOTE_POOLS: Record<string, string> = {
    cheer: 'emote_cheer',
    taunt: 'emote_taunt',
    point: 'emote_point',
    follow: 'emote_follow',
    hold: 'emote_hold',
    bye: 'emote_farewell',
    laugh: 'emote_laugh',
    // Own pool since 2026-10-13 (it played emote_annoyed's threats): a performed v4 [sighs].
    sigh: 'emote_sigh',
    no: 'emote_cant_do',
    heal: 'emote_need_healing',
    charge: 'emote_battle_start',
    trap: 'emote_trap_spotted',
    loot: 'emote_loot',
    crit: 'emote_crit',
    fumble: 'emote_fumble',
    levelup: 'emote_levelup',
    // Social barks (owner 2026-09-16), styled per voice. The three bows share one courtesy pool.
    wave: 'emote_greeting',
    bow: 'emote_bow',
    quickbow: 'emote_bow',
    hattip: 'emote_bow',
    salute: 'emote_salute',
    agree: 'emote_agree',
    shrug: 'emote_shrug',
    // Second promotion batch (owner 2026-09-16): moods that already have a recorded pool reuse it.
    mock: 'emote_taunt',
    celebrate: 'emote_cheer',
    despair: 'emote_despair', // own pool since 2026-10-13 (v4 [groans, dismayed]); it played emote_annoyed
    stomp: 'emote_annoyed',
    // Voiced 2026-10-13 (director): the everyday emotes that read as a line, on eleven_v4 with a manner/sound tag.
    sniff: 'emote_sniff',
    stinky: 'emote_stinky',
    drink: 'emote_drink',
    eat: 'emote_eat',
    pray: 'emote_pray',
    sleep: 'emote_sleep',
    coin: 'emote_coin',
    warm: 'emote_warm',
    tend: 'emote_tend',
    stagger: 'emote_stagger',
};

const VOICE_SLUG = /^[a-z0-9_]+$/;

/**
 * Resolve an emote's bark WITHOUT playing it, so the caller can start it on the gesture's first frame. `voice` is the
 * slug the server resolved, and a server null is AUTHORITATIVE silence (voiceless or silenced). Only the sender, who
 * performs before the server answers, passes `resolveLocally` to read the slug from the page's own encounter blob
 * (entityVoiceSlug). Resolves null when there is nothing to say.
 */
async function prepareChatEmoteVoice(voice: string | null, entityId: string, emote: string, seed: string,
                                     resolveLocally = false): Promise<(() => void) | null> {
    const pool = EMOTE_POOLS[emote];
    if (!pool) return null;
    try {
        let slug = String(voice ?? '').trim();
        if (!slug) {
            if (!resolveLocally) return null;
            const catalog = await loadAudioCatalog();
            slug = catalog ? entityVoiceSlug(catalog, entityId) : '';
        }
        if (!VOICE_SLUG.test(slug)) return null;
        const shard = await loadVoiceShard(slug);
        if (!shard) return null;
        // The seed suffix is unchanged from the original one-step player, so a line's take did not move.
        const take = pickAudioTake(shard, emoteTiers(slug, pool), seededRandom(`${seed}|chat-emote`));
        return take ? () => playChatVoiceLine(take.url) : null;
    } catch {
        return null; // a missing bark never blocks the gesture or the line
    }
}

/** Speak one emote now. Kept for callers that have no gesture to wait for. */
async function playChatEmoteVoice(voice: string, emote: string, seed: string): Promise<void> {
    const play = await prepareChatEmoteVoice(voice, '', emote, seed);
    play?.();
}

export function handleFt3dChat(data: ChatMessage): void {
    if (data.schema !== 'ft.ft3d.chat.v1' || !checkIfValidUUID(String(data.message_id ?? ''))) return;
    savedChatOutbox.delete(data.message_id);
    window.dispatchEvent(new CustomEvent('ft:ft3d-chat', { detail: data }));
}

// Other bundles borrow this page's socket and held-state planner through one window bridge.
Object.assign(window, { ftPublishSavedChat: async (message: ChatMessage, actorId: string, speech?: Record<string, unknown>): Promise<void> => {
    const operation_id = message.message_id;
    const encounterId = String(message.encounter_id ?? '');
    const reveals = speech?.reveals as Array<{ status_id: string; before: Record<string, unknown> }> | undefined;
    let applied_frame: AppliedFrameResult | undefined;
    if (reveals?.length) {
        const encounter = actorEncounterFor(encounterId), actor = actorRowFor(encounterId, actorId);
        if (!encounter || !actor) throw new Error('speech:held-actor-required');
        const read = abilityTruthReaders(actor, encounter, new Set<string>());
        applied_frame = gmStatusAppliedFrame({ operation_id, encounter_id: encounterId, actor_id: actorId,
            rev: Number(rowScalar(actor, 'movement_rev') ?? 0), member_revs: {} }, {
            rows: [], ec: {}, statusesAdded: [], statusStacks: [], charges: [], playerCharactersChanged: [],
            statusesRemoved: reveals.map(row => ({ status_id: row.status_id, status_item_id: String(row.before.status_item_id),
                target: String(row.before.status_target_id), before: row.before })),
        }, encounter, abilityTruthCampaign(encounter), read.definition);
    }
    const frame = { ...message, operation_id, actor_id: actorId, encounter_id: encounterId,
        transport_scope: ['ooc', 'whisper', 'gm'].includes(message.channel) ? 'campaign' : 'encounter',
        ...(applied_frame ? { applied_frame } : {}) } as EphemeralTruth;
    const pending = { wire: truthWireBytes(frame) };
    savedChatOutbox.set(operation_id, pending);
    sendSavedChat(pending);
    if (applied_frame) await handleBasePatch({ type: 'base_patch', encounter_id: encounterId, ops: [], applied: applied_frame });
} });

/** An emote key is a bare word — gesture-only emotes (dance, clap, nod) have no pool but still perform. */
const EMOTE_KEY = /^[a-z]{1,24}$/;

export function handleFt3dEmoteCue(data: EmoteCueMessage): void {
    if (data.schema !== 'ft.ft3d.emote-cue.v1' || !checkIfValidUUID(String(data.cue_id ?? ''))
        || !checkIfValidUUID(String(data.entity_id ?? '')) || !EMOTE_KEY.test(String(data.emote ?? ''))) return;
    window.dispatchEvent(new CustomEvent('ft:ft3d-emote-cue', { detail: data }));
}

const w = window as unknown as {
    ftPlayChatEmoteVoice?: typeof playChatEmoteVoice;
    ftPrepareChatEmoteVoice?: typeof prepareChatEmoteVoice;
};
w.ftPlayChatEmoteVoice = playChatEmoteVoice;
w.ftPrepareChatEmoteVoice = prepareChatEmoteVoice;
window.handleFt3dChat = handleFt3dChat;
window.handleFt3dEmoteCue = handleFt3dEmoteCue;

export { playChatEmoteVoice, prepareChatEmoteVoice };
